// PathMaterialFeature — Set12 (path-driven material assignment). DB-free data class: lives on
// `ObjPath.materialFeature` (a sibling of `terrainFeature`), flows through the
// `ResolveMapObjForTerrain`/`PackageUpMapObjForTerrain` whitelist, and is consumed by
// `collectMaterialRegions` (Terrain/Materials/Splat/MaterialRegions.ts). "DB-free" = no
// mobx-graphlink / web-vcore deps, so the Node test lane can import it directly.
import type {ParamValue} from "./@MaterialTypes.js";

/** Optional MASKS narrowing where a region applies (Set12 §3.5; evaluation = P6, DONE
 *  2026-08-10).  A mask MULTIPLIES the region's weight, so e.g. an `elevationBand` mask
 *  confines a river-stroke to a canyon floor ("only apply to certain elevations inside").
 *  Evaluated per sample on the RASTERISER (from the chunk's elevation/slope lattices) and
 *  baked into the stored signed distance (`maskedDistance` — see MaterialRegions.ts), so the
 *  shader needs no mask data.  The feather default is kind-aware: elevationBand 2 m,
 *  slopeBand 0.1 m/m (~6°). */
export type MaterialMask =
	| {kind: "elevationBand"; min: number; max: number; feather?: number|n}
	| {kind: "slopeBand"; min: number; max: number; feather?: number|n};

/** Set12 — a path's material-assignment feature (`ObjPath.materialFeature`, sibling of
 *  `terrainFeature`). Where the path's geometry becomes a material region: "fill" paints inside
 *  a closed path; "stroke" paints within `strokeWidth` metres of any path (closed or open);
 *  "fillAndStroke" = the interior PLUS the stroke apron (one region, one SDF — the interior
 *  distance shifted by `strokeWidth`, §3.3). */
export class PathMaterialFeature {
	constructor(data?: Partial<PathMaterialFeature>) { Object.assign(this, data); }

	/** Whether `mf` is "active" — contributes a material region: true iff `!mf.disabled` AND
	 *  `recipeID` is set. Null returns false. Mirrors `PathTerrainFeature.IsActive`. */
	static IsActive(mf: PathMaterialFeature | n): mf is PathMaterialFeature {
		if (mf == null) return false;
		if (mf.disabled) return false;
		return mf.recipeID != "";
	}

	/** When true, the feature is preserved in the persisted data but ignored by every consumer
	 *  (bridge/rasterizer treat it as inert). Mirrors `PathTerrainFeature.disabled`. */
	disabled?: boolean|n;
	/** Which recipe (catalogue id) this path paints. */
	recipeID = "";
	/** How the path's geometry becomes a region. "fill"/"fillAndStroke" require the path to be
	 *  closed (`_curve_closed_resolved`); on an open path the bridge treats them as a data error
	 *  (no interior exists) and drops the region rather than inventing a stroke width. */
	mode: "fill" | "stroke" | "fillAndStroke" = "fill";
	/** FULL-width in metres (the total stroke thickness). The region extends `strokeWidth/2` on
	 *  EACH side of the polyline. Required for "stroke" / "fillAndStroke"; ignored for plain
	 *  "fill" (the strokeWidth-0 case). */
	strokeWidth?: number|n;
	/** Blend-band half-width in metres at this region's boundary. 0 = hard edge. */
	feather = 2;
	/** Falloff curve inside the feather band. Absent → the map's `blendDefaults.featherCurve`. */
	featherCurve?: "smooth" | "linear" | "step" | n;
	/** Explicit dominance (HIGHER wins — matches `PathTerrainFeature.dominance`). Absent =>
	 *  derived from containment nesting (§3.4): containment depth among interior-bearing regions,
	 *  or a default above all fills for strokes. */
	dominance?: number|n;
	/** When true, this region hard-overrides everything of lower dominance (no blend at all). */
	opaque?: boolean|n;
	/** Set12 followup (2026-08-25) — when true, this region opts into the P5 smin soft-union
	 *  when paired with ANOTHER soft-union region of EQUAL dominance.  Default (false/absent) =
	 *  equal-dominance regions use the ordered paint-over (the same deterministic path as
	 *  distinct dominance — a far-but-bbox-overlapping equal-dominance region's distance → 0, so
	 *  it can't bleed under the fading region; the "water replaces base" footgun).  A pair
	 *  soft-unions ONLY when EVERY region on BOTH slots set this — so an unrelated equal-
	 *  dominance material sharing a slot (or a far one in the adjacent slot) can never be
	 *  accidentally soft-unioned with it (see the shader's u_slotSoftUnion gate). */
	softUnion?: boolean|n;
	/** Boundary noise: warps the region edge (metres of displacement + feature size in metres).
	 *  Evaluated per fragment in P5; the broad-phase bbox inflates by `amplitude`. */
	edgeNoise?: {amplitude: number; scale: number}|n;
	/** Per-region overrides for the recipe's `regionModulated` params (the Set9 §6 mechanism). */
	paramModulations?: {[paramKey: string]: ParamValue}|n;
	/** Optional additional MASKS narrowing where this region applies (§3.5; evaluation = P6,
	 *  DONE 2026-08-10 — baked into the stored distances by the rasteriser). */
	masks?: MaterialMask[]|n;
}

/** Global blend defaults on `Mod_TerrainMaterials` — per-path `feather`/`featherCurve` fall back
 *  to these when absent. DB-free (consumed by the renderer + rasteriser). */
export class MaterialBlendDefaults {
	constructor(data?: Partial<MaterialBlendDefaults>) { Object.assign(this, data); }
	feather = 2;
	featherCurve?: "smooth" | "linear" | "step" | n;
}
